import { getApiUrl } from "@/lib/config";

export interface AssistantAttachment {
  kind: "file" | "page" | "action" | string;
  title: string;
  /** file: API path served with the user's token; page: route inside the app; action: API path of the pending action */
  url: string;
  documentId?: string | null;
  subtitle?: string | null;
  /** action cards: cancel_document | rename_signer | replace_document */
  action?: string | null;
  details?: string[] | null;
}

export interface AssistantImage {
  id: string;
  fileName: string;
  /** API path served with the user's token */
  url: string;
}

export interface AssistantMessage {
  role: "user" | "assistant";
  text: string;
  at: string;
  mood?: string | null;
  attachments: AssistantAttachment[];
  images?: AssistantImage[];
}

export interface AssistantActionResponse {
  ok: boolean;
  /** done | dismissed | not_found | expired | not_allowed | invalid | failed */
  status: string;
  error?: string | null;
  message?: AssistantMessage | null;
}

export interface AssistantTurn {
  conversationId: string;
  /** agent | offtopic | limit | unavailable | disabled | busy | empty | handoff */
  outcome: string;
  reply: AssistantMessage;
}

export interface AssistantStatus {
  enabled: boolean;
  publicEnabled: boolean;
  model: string;
  helpUrl: string;
}

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export class AssistantHttpError extends Error {
  constructor(public status: number, message?: string) {
    super(message || `HTTP ${status}`);
  }
}

export async function fetchAssistantStatus(): Promise<AssistantStatus | null> {
  try {
    const res = await fetch(getApiUrl("/api/assistant/status"));
    return res.ok ? await res.json() : null;
  } catch {
    return null;
  }
}

export async function sendAssistantMessage(message: string, conversationId: string | null, lang: string, imageIds: string[] = [], signal?: AbortSignal): Promise<AssistantTurn> {
  const res = await fetch(getApiUrl("/api/assistant/chat"), {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ message, conversationId, lang, imageIds }),
    signal,
  });
  if (!res.ok) throw new AssistantHttpError(res.status);
  return res.json();
}

/** Uploads a screenshot to send with the next message. */
export async function uploadAssistantImage(file: File): Promise<AssistantImage> {
  const body = new FormData();
  body.append("file", file, file.name);
  const res = await fetch(getApiUrl("/api/assistant/uploads"), { method: "POST", headers: authHeaders(), body });
  if (!res.ok) throw new AssistantHttpError(res.status);
  return res.json();
}

/** Loads a private image (own uploads) as an object URL for <img>. */
export async function fetchAssistantImageUrl(image: AssistantImage): Promise<string> {
  const res = await fetch(getApiUrl(image.url), { headers: authHeaders() });
  if (!res.ok) throw new AssistantHttpError(res.status);
  return URL.createObjectURL(await res.blob());
}

/** Buttons of an action card: confirm / dismiss, or the file of a "replace document" card. */
export async function runAssistantAction(actionUrl: string, verb: "confirm" | "dismiss"): Promise<AssistantActionResponse> {
  const res = await fetch(getApiUrl(`${actionUrl}/${verb}`), { method: "POST", headers: authHeaders() });
  if (!res.ok) throw new AssistantHttpError(res.status);
  return res.json();
}

export async function uploadForAssistantAction(actionUrl: string, file: File): Promise<AssistantActionResponse> {
  const body = new FormData();
  body.append("file", file, file.name);
  const res = await fetch(getApiUrl(`${actionUrl}/upload`), { method: "POST", headers: authHeaders(), body });
  if (!res.ok) throw new AssistantHttpError(res.status);
  return res.json();
}

export async function loadAssistantConversation(conversationId: string): Promise<{ conversationId: string; messages: AssistantMessage[] } | null> {
  const res = await fetch(getApiUrl(`/api/assistant/conversation/${encodeURIComponent(conversationId)}`), { headers: authHeaders() });
  if (res.status === 404) return null;
  if (!res.ok) throw new AssistantHttpError(res.status);
  return res.json();
}

export async function forgetAssistantConversation(conversationId: string): Promise<void> {
  try {
    await fetch(getApiUrl(`/api/assistant/conversation/${encodeURIComponent(conversationId)}`), { method: "DELETE", headers: authHeaders() });
  } catch { /* the server forgets it on its own after the TTL */ }
}

/** Downloads a chat file with the user's token (the URL is an API path, never a public link). */
export async function fetchAssistantFile(attachment: AssistantAttachment): Promise<File> {
  const res = await fetch(getApiUrl(attachment.url), { headers: authHeaders() });
  if (!res.ok) throw new AssistantHttpError(res.status);
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  let name = star ? safeDecode(star[1]) : plain ? plain[1] : `${attachment.title}.pdf`;
  // Browsers drop the download name when it carries accents; keep it plain ASCII.
  name = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^\w.\- ]+/g, "_").trim() || "documento.pdf";
  if (!/\.pdf$/i.test(name)) name += ".pdf";
  return new File([blob], name, { type: blob.type || "application/pdf" });
}

function safeDecode(s: string) {
  try { return decodeURIComponent(s); } catch { return s; }
}

export function saveFile(file: File) {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
